import anime from 'animejs'
import Controller from './controller'
import { isReducedMotion } from '../utilities'

export default class extends Controller {
  play() {
    if (isReducedMotion()) {
      return
    }

    this.methodId = this.data.get('id')

    if (typeof this[this.methodId] !== 'function') {
      this.show()
      console.warn(`Shapes method ${this.methodId} does not exist`)
      return
    }

    this.setShapes()
    this.setSize()

    // Play animation
    setTimeout(() => {
      this[this.methodId]()
    }, 0)
  }

  animate(animation) {
    animation()
    this.show()
  }

  show() {
    this.element.style.opacity = 1
  }

  /* Get all child `[data-shape]` elements and store a reference to their node and position (rect) */
  /* `this.getShape(id)` is used to retrieve a shape by ID */
  setShapes() {
    this.shapes = {}

    for (const element of Array.from(this.element.querySelectorAll('[data-shape]'))) {
      const rect = element.getBoundingClientRect()

      this.shapes[element.dataset.shape] = {
        element,
        rect,
      }
    }
  }

  setSize() {
    this.svgRect = this.element.getBoundingClientRect()
    this.viewBoxWidth = this.element.width.baseVal.value
    this.viewBoxHeight = this.element.height.baseVal.value
  }

  getShape(id) {
    return this.shapes[id]
  }

  /* Get transform origin for a shape relative to itself, rather than the SVG document */
  /* This makes SVG element transforms behave more like DOM element transforms */
  getOrigin(shape, x, y, offsetX = 0, offsetY = 0) {
    const relativeOffsetX = offsetX * (this.svgRect.width / this.viewBoxWidth)
    const relativeOffsetY = offsetY * (this.svgRect.height / this.viewBoxHeight)

    const originX =
      (shape.rect.left - this.svgRect.left - relativeOffsetX + shape.rect.width * (x / 100)) /
      this.svgRect.width
    const originY =
      (shape.rect.top - this.svgRect.top - relativeOffsetY + shape.rect.height * (y / 100)) /
      this.svgRect.height

    return `${this.viewBoxWidth * originX}px ${this.viewBoxHeight * originY}px`
  }

  spring(mass = 1, stiffness = 100, damping = 10, velocity = 0) {
    return `spring(${mass}, ${stiffness}, ${damping}, ${velocity})`
  }

  semicircles() {
    const group = this.getShape('group')
    const circleInner = this.getShape('circleInner')
    const semicircles = this.getShape('semicircles')
    const semicircle = this.getShape('semicircle')

    anime.set(group.element, {
      transformOrigin: this.getOrigin(group, 20, 100),
    })
    anime.set(circleInner.element, {
      transformOrigin: this.getOrigin(circleInner, 0, 50),
    })
    anime.set(semicircles.element, {
      transformOrigin: this.getOrigin(semicircles, 0, 50),
    })
    anime.set(semicircle.element, {
      transformOrigin: this.getOrigin(semicircle, 0, 50),
    })

    this.animate(() => {
      anime({
        targets: group.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0.4, 1], easing: this.spring(0.6, 100, 10, 0), duration: 5000 },
        rotate: { value: [-45, 0], easing: 'easeOutExpo', duration: 1300 },
      })
      anime({
        targets: circleInner.element,
        rotate: { value: [-45, 0], easing: this.spring(1, 50, 5, 0), duration: 5000 },
      })
      anime({
        targets: semicircles.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: 'easeOutQuart', duration: 700 },
        delay: 300
      })
      anime({
        targets: semicircle.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: 'easeOutQuart', duration: 700 },
        delay: 450,
      })
    })
  }

  whoWeAre() {
    const pacmanGroup = this.getShape('pacmanGroup')
    const pacman = this.getShape('pacman')
    const semicircle = this.getShape('semicircle')
    const circleSmall = this.getShape('circleSmall')
    const circleBig = this.getShape('circleBig')

    anime.set(pacmanGroup.element, {
      transformOrigin: this.getOrigin(pacman, 50, 50),
    })
    anime.set(circleBig.element, {
      transformOrigin: this.getOrigin(circleBig, 0, 100),
    })
    anime.set(semicircle.element, {
      transformOrigin: this.getOrigin(semicircle, 100, 50),
    })

    this.animate(() => {
      anime({
        targets: pacmanGroup.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: ['50%', '-0.1%'], easing: this.spring(2, 55, 10, 0), duration: 5000 },
        rotate: { value: [90, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      })
      anime({
        targets: circleSmall.element,
        delay: 500,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: ['-25%', 0], easing: 'easeOutBounce', duration: 1000 },
      })
      anime({
        targets: circleBig.element,
        delay: 700,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0.5, 1], easing: 'easeOutCubic', duration: 800 },
        translateX: { value: ['10%', 0], easing: 'easeOutCubic', duration: 800 },
        translateY: { value: ['-30%', 0], easing: 'easeOutBounce', duration: 1000 },
      })
      anime({
        delay: 900,
        targets: semicircle.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: ['-25%', 0], easing: this.spring(1.3, 55, 10, 0), duration: 5000 },
        rotate: { value: [-45, 0], easing: this.spring(1.3, 55, 10, 0), duration: 5000 },
      })
    })
  }

  insights() {
    const background = this.getShape('background')
    const backgroundInner = this.getShape('backgroundInner')
    const backgroundSemicircle = this.getShape('backgroundSemicircle')
    const circleGroup = this.getShape('circleGroup')
    const circleInner = this.getShape('circleInner')

    anime.set(background.element, {
      transformOrigin: this.getOrigin(background, 100, 50),
    })
    anime.set(backgroundInner.element, {
      transformOrigin: this.getOrigin(backgroundInner, 100, 50),
    })
    anime.set(backgroundSemicircle.element, {
      transformOrigin: this.getOrigin(backgroundSemicircle, 100, 100),
    })
    anime.set(circleGroup.element, {
      transformOrigin: this.getOrigin(circleGroup, 50, 50),
    })
    anime.set(circleInner.element, {
      transformOrigin: this.getOrigin(circleInner, 50, 50),
    })

    this.animate(() => {
      anime({
        targets: background.element,
        scaleX: { value: [0, 1], easing: 'easeOutQuint', duration: 1000 },
      })
      anime({
        delay: 200,
        targets: backgroundInner.element,
        scaleX: { value: [0, 1], easing: 'easeOutQuint', duration: 1000 },
      })
      anime({
        delay: 300,
        targets: backgroundSemicircle.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 1000 },
      })

      anime({
        delay: 200,
        targets: circleGroup.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0.1, 1], easing: this.spring(1.1, 70, 10, 0), duration: 5000 },
      })
      anime({
        delay: 300,
        targets: circleInner.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0.1, 1], easing: this.spring(1.1, 70, 10, 0), duration: 5000 },
        rotate: { value: [-90, 1], easing: this.spring(1.5, 70, 10, 0), duration: 5000 },
      })
    })
  }

  events() {
    const circleBigGroup = this.getShape('circleBigGroup')
    const circleBig = this.getShape('circleBig')
    const circleSmallGroup = this.getShape('circleSmallGroup')
    const circleSmall = this.getShape('circleSmall')
    const towerCircleGroup = this.getShape('towerCircleGroup')
    const towerCircle = this.getShape('towerCircle')
    const semicircleTopGroup = this.getShape('semicircleTopGroup')
    const semicircleTop = this.getShape('semicircleTop')
    const semicircleBottom = this.getShape('semicircleBottom')
    const ball = this.getShape('ball')

    anime.set(circleSmallGroup.element, {
      transformOrigin: this.getOrigin(circleBig, 50, 50),
    })
    anime.set(circleSmall.element, {
      transformOrigin: this.getOrigin(circleSmall, 50, 50),
    })
    anime.set(circleBig.element, {
      transformOrigin: this.getOrigin(circleBig, 50, 0),
    })

    anime.set(towerCircle.element, {
      transformOrigin: this.getOrigin(towerCircle, 50, 100),
    })
    anime.set(semicircleTop.element, {
      transformOrigin: this.getOrigin(semicircleTop, 50, 100),
    })
    anime.set(semicircleTopGroup.element, {
      translateY: 132,
    })
    anime.set(semicircleBottom.element, {
      transformOrigin: this.getOrigin(semicircleBottom, 50, 100),
    })

    this.animate(() => {
      const timeline = anime.timeline()
      timeline.add({
        targets: towerCircle.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0.2, 1], easing: 'easeOutQuint', duration: 700 },
      })
      timeline.add({
        targets: towerCircleGroup.element,
        translateY: { value: [264, 132], easing: 'easeOutQuint', duration: 700 },
      }, 350)
      timeline.add({
        targets: semicircleTop.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 700 },
      }, 350)
      timeline.add({
        targets: semicircleBottom.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 700 },
      }, 700)
      timeline.add({
        targets: [towerCircleGroup.element, semicircleTopGroup.element],
        translateY: { value: 0, easing: 'easeOutQuint', duration: 700 },
      }, 700)
      timeline.add({
        targets: circleBig.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 1050 },
      }, 0)
      timeline.add({
        targets: circleBigGroup.element,
        translateY: { value: [-47, 0], easing: 'easeOutQuint', duration: 700 },
      }, 350)
      timeline.add({
        targets: circleSmall.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 700 },
      }, 350)
      timeline.add({
        targets: circleSmallGroup.element,
        rotate: { value: [-25, 0], easing: this.spring(2, 70, 10, 0), duration: 5000 },
      }, 350)

      anime({
        targets: ball.element,
        delay: 350,
        translateY: { value: [-312, 0], easing: 'easeOutBounce', duration: 1200 },
      })
    })
  }

  training() {
    const pacmanGroup = this.getShape('pacmanGroup')
    const pacman = this.getShape('pacman')
    const ball = this.getShape('ball')
    const circleGroup = this.getShape('circleGroup')
    const circleTL = this.getShape('circleTL')
    const circleTR = this.getShape('circleTR')
    const circleBR = this.getShape('circleBR')
    const circleBL = this.getShape('circleBL')

    anime.set(pacmanGroup.element, {
      transformOrigin: this.getOrigin(pacman, 50, 50),
      opacity: 0
    })
    anime.set(circleGroup.element, {
      transformOrigin: this.getOrigin(circleGroup, 50, 50),
    })

    const radius = 77.75

    anime.set(circleTR.element, {
      translateY: radius * 2,
    })
    anime.set(circleBR.element, {
      transformOrigin: this.getOrigin(circleBR, 50, 50),
    })
    anime.set(circleBL.element, {
      transformOrigin: this.getOrigin(circleBL, 50, 50),
    })
    anime.set(circleTL.element, {
      transformOrigin: this.getOrigin(circleTL, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()
      const duration = 500
      const easing = 'easeInOutQuad'

      timeline.add({
        targets: circleTR.element,
        translateX: { value: radius * -2, easing, duration },
      })
      timeline.add({
        targets: circleTR.element,
        translateY: { value: 0, easing, duration },
      })
      timeline.add({
        targets: circleTR.element,
        translateX: { value: 0, easing, duration },
      })
      timeline.add({
        targets: circleBR.element,
        scale: { value: [0, 1], easing, duration },
      }, 0)
      timeline.add({
        targets: circleBL.element,
        scale: { value: [0, 1], easing, duration },
      }, duration)
      timeline.add({
        targets: circleTL.element,
        scale: { value: [0, 1], easing, duration },
      }, duration * 2)

      timeline.add({
        targets: circleGroup.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0.5, 1], easing: 'easeOutQuad', duration: 1500 },
        rotate: { value: [180, 1], easing: this.spring(2, 40, 10, 0), duration: 5000 },
      }, 200)

      timeline.add({
        targets: pacmanGroup.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [125, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
        rotate: { value: [75, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 0)
      timeline.add({
        targets: ball.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [-100, 0], easing: 'easeOutBounce', duration: 1200 },
        translateX: { value: [50, 0], easing: 'easeInOutQuad', duration: 1000 },
      }, 300)
    })
  }

  careers() {
    const circleDark = this.getShape('circleDark')
    const circleOrange = this.getShape('circleOrange')
    const circleTeal = this.getShape('circleTeal')

    anime.set(circleDark.element, {
      transformOrigin: this.getOrigin(circleDark, 100, 100)
    })
    anime.set(circleOrange.element, {
      transformOrigin: this.getOrigin(circleOrange, 50, 100)
    })
    anime.set(circleTeal.element, {
      transformOrigin: this.getOrigin(circleTeal, 50, 100)
    })

    this.animate(() => {
      const timeline = anime.timeline()
      const y = 50
      const delay = 150
      const scale = 0.7

      timeline.add({
        targets: circleTeal.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scale: { value: [scale, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      })
      timeline.add({
        targets: circleDark.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scale: { value: [scale, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, delay)
      timeline.add({
        targets: circleOrange.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scale: { value: [scale, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, delay * 2)
    })
  }

  advisoryBoard() {
    const semiGroup = this.getShape('semiGroup')
    const semiLeft = this.getShape('semiLeft')
    const semiMiddle = this.getShape('semiMiddle')
    const semiRight = this.getShape('semiRight')
    const ballLeft = this.getShape('ballLeft')
    const ballRight = this.getShape('ballRight')

    anime.set(semiGroup.element, {
      transformOrigin: this.getOrigin(semiGroup, 50, 50),
    })
    anime.set(ballLeft.element, {
      transformOrigin: this.getOrigin(ballLeft, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: semiLeft.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        rotate: { value: [45, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 400)

      timeline.add({
        targets: semiMiddle.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        rotate: { value: [45, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 500)

      timeline.add({
        targets: semiRight.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        rotate: { value: [45, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 600)

      timeline.add({
        targets: ballLeft.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 500)
      timeline.add({
        targets: ballRight.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 600)
    })
  }

  excellenceAwards() {
    const base = this.getShape('base')
    const middleGroup = this.getShape('middleGroup')
    const middleInner = this.getShape('middleInner')
    const middle = this.getShape('middle')
    const semiTop = this.getShape('semiTop')
    const ball = this.getShape('ball')
    const pacmanLeft = this.getShape('pacmanLeft')
    const pacmanRight = this.getShape('pacmanRight')

    anime.set(base.element, {
      transformOrigin: this.getOrigin(base, 50, 100),
    })
    anime.set(middleGroup.element, {
      transformOrigin: this.getOrigin(middle, 50, 100),
    })
    anime.set(middleInner.element, {
      transformOrigin: this.getOrigin(middle, 50, 0),
    })
    anime.set(semiTop.element, {
      transformOrigin: this.getOrigin(semiTop, 50, 100),
    })
    anime.set(pacmanLeft.element, {
      transformOrigin: this.getOrigin(pacmanLeft, 50, 50),
    })
    anime.set(pacmanRight.element, {
      transformOrigin: this.getOrigin(pacmanRight, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: middleInner.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [150, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
        rotate: { value: [45, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      })

      timeline.add({
        targets: base.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 800 },
      }, 350)
      timeline.add({
        targets: middleGroup.element,
        translateY: { value: [134, 0], easing: 'easeOutQuint', duration: 800 },
      }, 350)
      timeline.add({
        targets: ball.element,
        delay: 350,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [-150, 0], easing: 'easeOutBounce', duration: 1000 },
      }, 400)
      timeline.add({
        targets: semiTop.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 800 },
      }, 1100)

      timeline.add({
        targets: pacmanLeft.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [-50, 0], easing: 'easeOutBounce', duration: 1000 },
        rotate: { value: [-45, 0], easing: 'easeOutBounce', duration: 1000 },
      }, 550)
      timeline.add({
        targets: pacmanRight.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [50, 0], easing: 'easeOutBounce', duration: 1000 },
        rotate: { value: [45, 0], easing: 'easeOutBounce', duration: 1000 },
      }, 700)
    })
  }

  marketingLeaders() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const circleOrange = this.getShape('circleOrange')
    const circleBlue = this.getShape('circleBlue')
    const circleDark = this.getShape('circleDark')
    const circleTeal = this.getShape('circleTeal')
    const circleWhite = this.getShape('circleWhite')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      })
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      const y = 150
      const start = 300
      const delay = 200

      timeline.add({
        targets: circleOrange.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start)
      timeline.add({
        targets: circleBlue.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [-y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + delay)
      timeline.add({
        targets: circleDark.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 2))
      timeline.add({
        targets: circleWhite.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [-y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 2))
      timeline.add({
        targets: circleTeal.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y * 0.8, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 3))
    })
  }

  programLeaders() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const circle1 = this.getShape('circle1')
    const circle2 = this.getShape('circle2')
    const circle3 = this.getShape('circle3')
    const circle4 = this.getShape('circle4')
    const circle5 = this.getShape('circle5')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      })
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      const y = 150
      const start = 300
      const delay = 100

      timeline.add({
        targets: circle1.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start)
      timeline.add({
        targets: circle2.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + delay)
      timeline.add({
        targets: circle3.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 2))
      timeline.add({
        targets: circle4.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 3))
      timeline.add({
        targets: circle5.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y * 0.8, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 4))
    })
  }

  revenueLeaders() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const semis = Array.from(group.element.querySelectorAll('[data-semi]')).map((semi) => ({
      element: semi,
      rect: semi.getBoundingClientRect(),
    }))
    const circles = Array.from(group.element.querySelectorAll('circle')).map((circle) => ({
      element: circle,
      rect: circle.getBoundingClientRect(),
    }))

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      })
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      semis.forEach((semi, i) => {
        anime.set(semi.element, {
          transformOrigin: this.getOrigin(semi, 50, i % 2 === 0 ? 0 : 100)
        })

        const semisStart = 100

        timeline.add({
          targets: semi.element,
          scaleY: { value: [-1, 1], easing: 'easeInOutQuint', duration: 1250 },
        }, semisStart + (i * 85))
      })

      circles.forEach((circle, i) => {
        anime.set(circle.element, {
          transformOrigin: this.getOrigin(circle, 50, 50)
        })

        const circlesStart = 300

        timeline.add({
          targets: circle.element,
          scale: { value: [0.5, 1], easing: this.spring(1, 100, 10, 0), duration: 5000 },
        }, circlesStart + (i * 100))
      })
    })
  }

  accountBasedLeaders() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const baseLeft = this.getShape('baseLeft')
    const middleLeftGroup = this.getShape('middleLeftGroup')
    const middleLeftInner = this.getShape('middleLeftInner')
    const middleLeft = this.getShape('middleLeft')
    const ballLeft = this.getShape('ballLeft')
    const baseRight = this.getShape('baseRight')
    const middleRightGroup = this.getShape('middleRightGroup')
    const middleRightInner = this.getShape('middleRightInner')
    const middleRight = this.getShape('middleRight')
    const ballRight = this.getShape('ballRight')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })
    anime.set(baseLeft.element, {
      transformOrigin: this.getOrigin(baseLeft, 50, 100),
    })
    anime.set(middleLeftGroup.element, {
      transformOrigin: this.getOrigin(middleLeft, 50, 75),
    })
    anime.set(middleLeftInner.element, {
      transformOrigin: this.getOrigin(middleLeft, 50, 25),
    })
    anime.set(ballLeft.element, {
      transformOrigin: this.getOrigin(middleLeft, 50, 25),
    })
    anime.set(baseRight.element, {
      transformOrigin: this.getOrigin(baseRight, 50, 0),
    })
    anime.set(middleRightGroup.element, {
      transformOrigin: this.getOrigin(middleRight, 50, 25),
    })
    anime.set(middleRightInner.element, {
      transformOrigin: this.getOrigin(middleRight, 50, 75),
    })
    anime.set(ballRight.element, {
      transformOrigin: this.getOrigin(middleRight, 50, 75),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      })
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      const startLeft = 700

      timeline.add({
        targets: middleLeftInner.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [100, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
        rotate: { value: [45, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, startLeft)
      timeline.add({
        targets: baseLeft.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 800 },
      }, startLeft + 350)
      timeline.add({
        targets: middleLeftGroup.element,
        translateY: { value: [102, 0], easing: 'easeOutQuint', duration: 800 },
      }, startLeft + 350)
      timeline.add({
        targets: ballLeft.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 800 },
        translateY: { value: [38, 0], easing: 'easeOutBounce', duration: 1000, delay: 500 },
      }, startLeft + 500)

      const startRight = 500

      timeline.add({
        targets: middleRightInner.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [-100, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
        rotate: { value: [45, 0], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, startRight)
      timeline.add({
        targets: baseRight.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 800 },
      }, startRight + 350)
      timeline.add({
        targets: middleRightGroup.element,
        translateY: { value: [-102, 0], easing: 'easeOutQuint', duration: 800 },
      }, startRight + 350)
      timeline.add({
        targets: ballRight.element,
        scale: { value: [0, 1], easing: 'easeOutQuint', duration: 800 },
        translateY: { value: [-41, 0], easing: 'easeOutBounce', duration: 1000, delay: 500 },
      }, startRight + 500)
    })
  }

  technology() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const ballLarge = this.getShape('ballLarge')
    const ball1 = this.getShape('ball1')
    const ball2 = this.getShape('ball2')
    const ball3 = this.getShape('ball3')
    const ball4 = this.getShape('ball4')
    const ball5 = this.getShape('ball5')
    const ball6 = this.getShape('ball6')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: ballLarge.element,
        delay: 350,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [-150, 0], easing: 'easeOutBounce', duration: 1000 },
      })

      timeline.add({
        targets: ball1.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      })

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      timeline.add({
        targets: ball2.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 500)

      timeline.add({
        targets: ball3.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 700)

      timeline.add({
        targets: ball4.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 900)

      timeline.add({
        targets: ball5.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 1000)

      timeline.add({
        targets: ball6.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 1200)
    })
  }

  professionalServices() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const circleLarge = this.getShape('circleLarge')
    const circleMiddle = this.getShape('circleMiddle')
    const circleSmall = this.getShape('circleSmall')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    anime.set(circleLarge.element, {
      transformOrigin: this.getOrigin(circleLarge, 50, 50),
    })

    anime.set(circleMiddle.element, {
      transformOrigin: this.getOrigin(circleMiddle, 50, 50),
    })

    anime.set(circleSmall.element, {
      transformOrigin: this.getOrigin(circleSmall, 50, 50),
    })


    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      timeline.add({
        targets: circleLarge.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 500)

      timeline.add({
        targets: circleMiddle.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 700)

      timeline.add({
        targets: circleSmall.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 900)
    })
  }

  financialServices() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const circleMiddle = this.getShape('circleMiddle')
    const semiLeft = this.getShape('semiLeft')
    const semiRight = this.getShape('semiRight')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    anime.set(circleMiddle.element, {
      transformOrigin: this.getOrigin(circleMiddle, 50, 50),
    })

    anime.set(semiRight.element, {
      transformOrigin: this.getOrigin(semiRight, 50, 100),
    })

    anime.set(semiLeft.element, {
      transformOrigin: this.getOrigin(semiLeft, 50, 100),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      timeline.add({
        targets: semiLeft.element,
        opacity: { value: [0, 1], duration: 450, easing: 'easeOutQuad' },
        translateX: { value: [-100, 0], easing: 'easeOutBounce', duration: 1000 },
      }, 900)

      timeline.add({
        targets: semiRight.element,
        opacity: { value: [0, 1], duration: 450, easing: 'easeOutQuad' },
        translateX: { value: [100, 0], easing: 'easeOutBounce', duration: 1000 },
      }, 900)

      timeline.add({
        targets: circleMiddle.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 300)

    })
  }

  consulting() {
    const right = this.getShape('right')
    const right1 = this.getShape('right1')
    const right2 = this.getShape('right2')
    const right3 = this.getShape('right3')
    const right4 = this.getShape('right4')
    const ballGroup = this.getShape('ballGroup')
    const ball = this.getShape('ball')

    anime.set(ballGroup.element, {
      transformOrigin: this.getOrigin(right, 0, 50),
    })
    anime.set(ball.element, {
      transformOrigin: this.getOrigin(ball, 10, 90),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      const start = 200

      timeline.add({
        targets: right4.element,
        translateY: { value: [-46, 0], easing: this.spring(1, 100, 12), duration: 5000 },
      }, start)
      timeline.add({
        targets: right3.element,
        translateY: { value: [-46, 0], easing: this.spring(1, 100, 12), duration: 5000 },
      }, start + 50)
      timeline.add({
        targets: right2.element,
        translateY: { value: [-46, 0], easing: this.spring(1, 100, 12), duration: 5000 },
      }, start + 100)
      timeline.add({
        targets: right1.element,
        translateY: { value: [-49, 0], easing: this.spring(1, 100, 12), duration: 5000 },
      }, start + 150)

      timeline.add({
        targets: ballGroup.element,
        rotate: { value: [-55, 0], easing: this.spring(2, 55, 15), duration: 5000 },
      }, start + 300)
      timeline.add({
        targets: ball.element,
        scale: { value: [0.5, 1], easing: 'easeOutQuart', duration: 1200 },
      }, start + 300)
    })
  }

  research() {
    const pacmanGroup = this.getShape('pacmanGroup')
    const pacman = this.getShape('pacman')
    const wedge = this.getShape('wedge')
    const circleOrange = this.getShape('circleOrange')
    const circleDark = this.getShape('circleDark')

    anime.set(pacmanGroup.element, {
      transformOrigin: this.getOrigin(pacman, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: pacmanGroup.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [400, 0], easing: this.spring(2, 55, 15, 0), duration: 5000 },
        rotate: { value: [90, 0], easing: this.spring(2, 55, 15, 0), duration: 5000 },
      }, 100)
      timeline.add({
        targets: wedge.element,
        translateX: { value: [-40.4, 0], easing: 'easeInOutQuart', duration: 1000 },
        translateY: { value: [44, 0], easing: 'easeInOutQuart', duration: 1000 },
        fill: { value: ['#008495', '#E2DBBE'], easing: 'easeInOutQuart', duration: 1000 },
      }, 200)

      timeline.add({
        targets: circleOrange.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [400, 0], easing: this.spring(2, 55, 15, 0), duration: 5000 },
      }, 0)

      timeline.add({
        targets: circleDark.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: [400, 0], easing: this.spring(2, 55, 15, 0), duration: 5000 },
      }, 200)
    })
  }

  learning() {
    const semis = Array.from(this.element.querySelectorAll('path')).map((semi) => ({
      element: semi,
      rect: semi.getBoundingClientRect(),
    }))

    this.animate(() => {
      const timeline = anime.timeline()

      semis.forEach((semi, i) => {
        anime.set(semi.element, {
          transformOrigin: this.getOrigin(semi, 50, i % 2 === 0 ? 100 : 0)
        })

        timeline.add({
          targets: semi.element,
          scale: { value: [0.5, 1], easing: 'easeOutQuart', duration: 1000 },
          opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' }
        }, i * 25)
      })
    })
  }

  implementation() {
    const background = this.getShape('background')
    const circle1 = this.getShape('circle1')
    const circle2 = this.getShape('circle2')
    const circle3 = this.getShape('circle3')
    const circle4 = this.getShape('circle4')
    const circle5 = this.getShape('circle5')
    const circle6 = this.getShape('circle6')

    anime.set(background.element, {
      transformOrigin: this.getOrigin(background, 50, 100)
    })

    anime.set(circle1.element, {
      transformOrigin: this.getOrigin(circle1, 50, 100)
    })
    anime.set(circle2.element, {
      transformOrigin: this.getOrigin(circle2, 50, 100)
    })
    anime.set(circle3.element, {
      transformOrigin: this.getOrigin(circle3, 50, 100)
    })
    anime.set(circle4.element, {
      transformOrigin: this.getOrigin(circle4, 50, 100)
    })
    anime.set(circle5.element, {
      transformOrigin: this.getOrigin(circle5, 50, 100)
    })
    anime.set(circle6.element, {
      transformOrigin: this.getOrigin(circle6, 50, 100)
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: background.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0.2, 1], easing: 'easeOutQuart', duration: 1250 },
      }, 0)

      const start = 100

      timeline.add({
        targets: circle1.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scaleY: { value: [0, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, start)
      timeline.add({
        targets: circle2.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [32, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scaleY: { value: [0.4, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, start + 50)
      timeline.add({
        targets: circle3.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [66, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scaleY: { value: [0.25, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, start + 100)
      timeline.add({
        targets: circle4.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [122, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scaleY: { value: [0.2, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, start + 150)
      timeline.add({
        targets: circle5.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [171, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scaleY: { value: [0.13, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, start + 200)
      timeline.add({
        targets: circle6.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [219, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        scaleY: { value: [0.1, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, start + 250)
    })
  }

  execute() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const shapes = this.getShape('shapes')
    const semiTop = this.getShape('semiTop')
    const semiBottom = this.getShape('semiBottom')
    const circleWhite = this.getShape('circleWhite')
    const circleOrange = this.getShape('circleOrange')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })
    anime.set(shapes.element, {
      transformOrigin: this.getOrigin(clip, 50, 100),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: shapes.element,
        scale: { value: [0.4, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, 400)

      timeline.add({
        targets: semiTop.element,
        translateX: { value: [49.6, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        translateY: { value: [8, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, 750)
      timeline.add({
        targets: semiBottom.element,
        translateX: { value: [-49.6, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        translateY: { value: [-8, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, 750)

      timeline.add({
        targets: circleOrange.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [-50, 0], easing: this.spring(1, 100, 15, 0), duration: 5000 },
      }, 1000)
      timeline.add({
        targets: circleWhite.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [50, 0], easing: this.spring(1, 100, 15, 0), duration: 5000 },
      }, 1000)
    })
  }

  build() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const circleBottom = this.getShape('circleBottom')
    const circleTop = this.getShape('circleTop')
    const semiWhite = this.getShape('semiWhite')
    const semiOrange = this.getShape('semiOrange')

    anime.set(circleBottom.element, {
      transformOrigin: this.getOrigin(circleBottom, 50, 50),
    })

    anime.set(circleTop.element, {
      transformOrigin: this.getOrigin(circleTop, 50, 50),
    })

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      timeline.add({
        targets: circleTop.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 300)

      timeline.add({
        targets: circleBottom.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [70, 0], easing: this.spring(2, 100, 20, 0), duration: 5000 },
      }, 300)

      timeline.add({
        targets: semiWhite.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: ['-25%', 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
        rotate: { value: [-45, 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
      }, 300)

      timeline.add({
        targets: semiOrange.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateX: { value: ['25%', 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
        rotate: { value: [-45, 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
      }, 500)
    })
  }

  drive() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const circleLarge = this.getShape('circleLarge')
    const circle1 = this.getShape('circle1')
    const circle2 = this.getShape('circle2')
    const circle3 = this.getShape('circle3')
    const circle4 = this.getShape('circle4')
    const circle5 = this.getShape('circle5')
    const circle6 = this.getShape('circle6')
    const circle7 = this.getShape('circle7')
    const circle8 = this.getShape('circle8')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    anime.set(circleLarge.element, {
      transformOrigin: this.getOrigin(circleLarge, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      timeline.add({
        targets: circleLarge.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 500)

      timeline.add({
        targets: circle1.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 600)

      timeline.add({
        targets: circle2.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 650)

      timeline.add({
        targets: circle3.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 700)

      timeline.add({
        targets: circle4.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 750)

      timeline.add({
        targets: circle5.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 800)

      timeline.add({
        targets: circle6.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 850)

      timeline.add({
        targets: circle7.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 900)

      timeline.add({
        targets: circle8.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        scale: { value: [0, 1], easing: this.spring(2, 55, 10, 0), duration: 5000 },
      }, 950)

    })
  }

  equip() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const semiBottom = this.getShape('semiBottom')
    const semiMiddle = this.getShape('semiMiddle')
    const semiTop = this.getShape('semiTop')
    const ballDrop = this.getShape('ballDrop')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      timeline.add({
        targets: semiBottom.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: ['25%', 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
      }, 300)

      timeline.add({
        targets: semiMiddle.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: ['25%', 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
      }, 500)

      timeline.add({
        targets: semiTop.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: ['25%', 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
      }, 700)

      timeline.add({
        targets: ballDrop.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: ['-20%', 0], easing: this.spring(1.3, 55, 10, 0), duration: 6000 },
      }, 1200)
    })
  }

  improve() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const shapes = this.getShape('shapes')
    const inner = this.getShape('inner')
    const circleTeal = this.getShape('circleTeal')
    const circleOrange = this.getShape('circleOrange')
    const circleLight = this.getShape('circleLight')
    const circleWhite1 = this.getShape('circleWhite1')
    const circleWhite2 = this.getShape('circleWhite2')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })
    anime.set(shapes.element, {
      transformOrigin: this.getOrigin(clip, 50, 100),
    })
    anime.set(inner.element, {
      transformOrigin: this.getOrigin(inner, 50, 50),
    })
    anime.set(circleTeal.element, {
      transformOrigin: this.getOrigin(circleTeal, 50, 50),
    })
    anime.set(circleOrange.element, {
      transformOrigin: this.getOrigin(circleOrange, 50, 15),
    })
    anime.set(circleLight.element, {
      transformOrigin: this.getOrigin(circleLight, 50, 50),
    })
    anime.set(circleWhite1.element, {
      transformOrigin: this.getOrigin(circleWhite1, 50, 50),
    })
    anime.set(circleWhite2.element, {
      transformOrigin: this.getOrigin(circleWhite2, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)
      timeline.add({
        targets: shapes.element,
        scale: { value: [0.4, 1], easing: this.spring(2, 100, 20, 0), duration: 5000 },
        translateY: { value: [100, 0], easing: 'easeOutQuart', duration: 1250 },
      }, 400)
      timeline.add({
        targets: inner.element,
        rotate: { value: [180, 0], easing: this.spring(2, 65, 20, 0), duration: 5000 },
      }, 400)

      const start = 300
      const delay = 150

      timeline.add({
        targets: circleTeal.element,
        opacity: { value: [0, 1], easing: 'easeOutQuart', duration: 1000 },
        scale: { value: [0.5, 1], easing: 'easeOutQuart', duration: 1000 },
      }, start)
      timeline.add({
        targets: circleOrange.element,
        opacity: { value: [0, 1], easing: 'easeOutQuart', duration: 1000 },
        scale: { value: [0.5, 1], easing: 'easeOutQuart', duration: 1000 },
      }, start + delay)
      timeline.add({
        targets: circleLight.element,
        opacity: { value: [0, 1], easing: 'easeOutQuart', duration: 1000 },
        scale: { value: [0.5, 1], easing: 'easeOutQuart', duration: 1000 },
      }, start + (delay * 2))
      timeline.add({
        targets: circleWhite1.element,
        opacity: { value: [0, 1], easing: 'easeOutQuart', duration: 1000 },
        scale: { value: [0.5, 1], easing: 'easeOutQuart', duration: 1000 },
      }, start + (delay * 3))
      timeline.add({
        targets: circleWhite2.element,
        opacity: { value: [0, 1], easing: 'easeOutQuart', duration: 1000 },
        scale: { value: [0.5, 1], easing: 'easeOutQuart', duration: 1000 },
      }, start + (delay * 4))
    })
  }

  accelerate() {
    const clip = this.getShape('clip')
    const group = this.getShape('group')
    const circle1 = this.getShape('circle1')
    const circle2 = this.getShape('circle2')
    const circle3 = this.getShape('circle3')
    const circle4 = this.getShape('circle4')

    anime.set(clip.element, {
      transformOrigin: this.getOrigin(clip, 50, 50),
    })

    this.animate(() => {
      const timeline = anime.timeline()

      timeline.add({
        targets: group.element,
        opacity: { value: [0, 1], easing: 'easeInOutQuart', duration: 1250 },
      })
      timeline.add({
        targets: clip.element,
        scale: { value: [0.3, 1], easing: 'easeInOutQuart', duration: 1250 },
      }, 0)

      const y = 150
      const start = 300
      const delay = 100

      timeline.add({
        targets: circle1.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start)
      timeline.add({
        targets: circle2.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + delay)
      timeline.add({
        targets: circle3.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 2))
      timeline.add({
        targets: circle4.element,
        opacity: { value: [0, 1], duration: 350, easing: 'easeOutQuad' },
        translateY: { value: [y, 0], easing: this.spring(2, 100, 16, 0), duration: 5000 },
      }, start + (delay * 3))
    })
  }
}
